import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Sparkles } from 'lucide-react';
import api from '../../lib/api';
import { jobRouteParam, jobUrlSegment } from '../../lib/slug';
import { Tag, Organization } from '../../types';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Textarea from '../../components/ui/Textarea';
import { Card, CardContent } from '../../components/ui/Card';
import TagSelector from '../../components/ui/TagSelector';
import { AIAssistantShell, type AIAssistantField } from '../../components/ai';
import { useAuthStore } from '../../stores/authStore';
import { googleUrlPlaceholder } from '../../lib/googleWorkspace';
import { track } from '../../lib/analytics';

export default function CreateJobPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isBrandUser = user?.role === 'BRAND';
  const jobsBase = isBrandUser ? '/brand/jobs' : '/agency/campaigns';
  const { id: rawId } = useParams();
  const editKey = rawId ? jobRouteParam(rawId) : null;
  const isEditMode = !!editKey;
  const [currentStep, setCurrentStep] = useState(1);
  const [brands, setBrands] = useState<Organization[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingAs, setSavingAs] = useState<'draft' | 'publish' | null>(null);
  const [rosterPreview, setRosterPreview] = useState<{ total: number; tagged: number } | null>(null);
  const [rosterPreviewLoading, setRosterPreviewLoading] = useState(false);

  const [showAI, setShowAI] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiResult, setAiResult] = useState<any>(null);

  const [formData, setFormData] = useState({
    // Step 1: Basics
    brandOrgId: '',
    title: '',
    description: '',
    demoVideoLink: '',
    exampleScript: '',
    rewardsJson: { cash: 0, product: '', commission: 0 },
    contractStart: '',
    contractEnd: '',
    
    // Step 2: Products
    products: [] as Array<{ id?: string; title: string; description: string; imageUrls: string[]; videoLinks: string[]; ecommerceLinks: string[] }>,
    
    // Step 3: Matching
    tagIds: [] as string[],
    targetDemographics: {
      majorityRace: '' as '' | 'malay' | 'chinese' | 'indian',
      sex: '' as '' | 'male' | 'female',
      sexThreshold: 50,
      ageGroup: '' as '' | 'age18_24' | 'age25_34' | 'age35_plus',
    },
    totalBudgetRM: 0,
    estimatedKOL: 10,
    broadcastMultiplier: 3,
    shortlistMultiplier: 1,
    objectives: '',
    targetAudience: '',
    keyMessages: '',
    contentTypesStr: '',
    hashtagsStr: '',
    complianceRequirements: '',
    deliverablesLines: '',
    managedByOrgId: '',
    googleBriefUrl: '',
    applyGoogleTemplates: false,
  });

  const [agencyOptions, setAgencyOptions] = useState<{ id: string; name: string }[]>([]);
  /** Inline validation when moving between steps or submitting (mirrors API rules where applicable). */
  const [stepInlineError, setStepInlineError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [user?.id]);

  useEffect(() => {
    if (!isBrandUser || editKey) return;
    const m = user?.memberships?.find((x) => x.organization?.type === 'BRAND');
    if (m?.organizationId) {
      setFormData((prev) => ({ ...prev, brandOrgId: m.organizationId }));
    }
  }, [isBrandUser, editKey, user?.memberships]);

  useEffect(() => {
    if (!editKey) return;
    (async () => {
      try {
        const res = await api.get(`/jobs/${encodeURIComponent(editKey)}`);
        const j = res.data;
        setFormData({
          brandOrgId: j.brandOrgId || '',
          title: j.title || '',
          description: j.description || '',
          demoVideoLink: j.demoVideoLink || '',
          exampleScript: j.exampleScript || '',
          rewardsJson: j.rewardsJson || { cash: 0, product: '', commission: 0 },
          contractStart: j.contractStart ? j.contractStart.slice(0, 10) : '',
          contractEnd: j.contractEnd ? j.contractEnd.slice(0, 10) : '',
          products: (j.products || []).map((p: any) => ({
            id: p.id,
            title: p.title || '',
            description: p.description || '',
            imageUrls: p.imageUrls || [],
            videoLinks: p.videoLinks || [],
            ecommerceLinks: p.ecommerceLinks || [],
          })),
          tagIds: (j.jobTags || []).map((jt: any) => jt.tagId || jt.tag?.id),
          targetDemographics: {
            majorityRace: (j.demographicMix as any)?.majorityRace || '',
            sex: (j.demographicMix as any)?.sex || '',
            sexThreshold: (j.demographicMix as any)?.sexThreshold || 50,
            ageGroup: (j.demographicMix as any)?.ageGroup || '',
          },
          totalBudgetRM: j.totalBudgetRM || 0,
          estimatedKOL: j.estimatedKOL || 10,
          broadcastMultiplier: j.broadcastMultiplier || 3,
          shortlistMultiplier: j.shortlistMultiplier || 1,
          objectives: j.objectives || '',
          targetAudience: j.targetAudience || '',
          keyMessages: j.keyMessages || '',
          contentTypesStr: Array.isArray(j.contentTypes) ? j.contentTypes.join(', ') : '',
          hashtagsStr: Array.isArray(j.hashtags) ? j.hashtags.join(', ') : '',
          complianceRequirements: j.complianceRequirements || '',
          deliverablesLines: Array.isArray(j.deliverablesChecklist)
            ? j.deliverablesChecklist.map((d: any) => d.label || d).join('\n')
            : '',
          managedByOrgId: j.managedByOrgId || '',
          googleBriefUrl: '',
          applyGoogleTemplates: false,
        });
      } catch (e) {
        console.error('Failed to load job for editing:', e);
      }
    })();
  }, [editKey]);

  const loadData = async () => {
    try {
      const [brandsRes, tagsRes] = await Promise.all([
        api.get('/organizations'),
        api.get('/tags'),
      ]);
      setBrands(brandsRes.data.filter((org: Organization) => org.type === 'BRAND'));
      setTags(tagsRes.data);

    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  useEffect(() => {
    if (user?.role !== 'BRAND') return;
    (async () => {
      try {
        const jobsRes = await api.get('/jobs');
        const agencies = new Map<string, string>();
        for (const j of jobsRes.data as Array<{ managedByOrgId?: string; managedByOrg?: { name?: string } }>) {
          if (j.managedByOrgId && j.managedByOrg?.name) {
            agencies.set(j.managedByOrgId, j.managedByOrg.name);
          }
        }
        setAgencyOptions([...agencies.entries()].map(([id, name]) => ({ id, name })));
      } catch {
        setAgencyOptions([]);
      }
    })();
  }, [user?.id, user?.role]);

  // Product analytics: one event per wizard step shown (no form content).
  useEffect(() => {
    track('campaign_wizard_step', { step: currentStep, mode: isEditMode ? 'edit' : 'create' });
  }, [currentStep, isEditMode]);

  const tagKey = formData.tagIds.join(',');
  useEffect(() => {
    if (isBrandUser || currentStep !== 3) return;
    const orgId = user?.organizationId;
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      setRosterPreviewLoading(true);
      try {
        const rosterRes = await api.get('/kols/search/manual', {
          params: { agencyOrgId: orgId, limit: 1 },
        });
        const total = Number(rosterRes.data.total ?? 0);
        let tagged = total;
        if (tagKey) {
          const taggedRes = await api.get('/kols/search/manual', {
            params: { agencyOrgId: orgId, tags: tagKey, limit: 1 },
          });
          tagged = Number(taggedRes.data.total ?? 0);
        }
        if (!cancelled) setRosterPreview({ total, tagged });
      } catch {
        if (!cancelled) setRosterPreview(null);
      } finally {
        if (!cancelled) setRosterPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentStep, tagKey, user?.organizationId, isBrandUser]);

  const handleSubmit = async (options?: { publish?: boolean }) => {
    const err1 = validateStep(1);
    if (err1) {
      setStepInlineError(err1);
      setCurrentStep(1);
      return;
    }
    const err3 = validateStep(3);
    if (err3) {
      setStepInlineError(err3);
      setCurrentStep(3);
      return;
    }
    setStepInlineError(null);

    try {
      setLoading(true);
      setSavingAs(options?.publish ? 'publish' : 'draft');

      const targetDemographics: any = {};
      if (formData.targetDemographics.majorityRace) {
        targetDemographics.majorityRace = formData.targetDemographics.majorityRace;
      }
      if (formData.targetDemographics.sex) {
        targetDemographics.sex = formData.targetDemographics.sex;
        targetDemographics.sexThreshold = formData.targetDemographics.sexThreshold;
      }
      if (formData.targetDemographics.ageGroup) {
        targetDemographics.ageGroup = formData.targetDemographics.ageGroup;
      }

      const contentTypes = formData.contentTypesStr
        .split(/[,;\n]/)
        .map((s) => s.trim())
        .filter(Boolean);
      const hashtags = formData.hashtagsStr
        .split(/[,;\n]/)
        .map((s) => s.trim())
        .filter(Boolean);
      const deliverablesChecklist = formData.deliverablesLines
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((label) => ({ label, done: false }));

      const payload = {
        brandOrgId: formData.brandOrgId,
        title: formData.title,
        description: formData.description,
        demoVideoLink: formData.demoVideoLink,
        exampleScript: formData.exampleScript,
        rewardsJson: formData.rewardsJson,
        contractStart: formData.contractStart || null,
        contractEnd: formData.contractEnd || null,
        demographicMix: Object.keys(targetDemographics).length > 0 ? targetDemographics : null,
        totalBudgetRM: formData.totalBudgetRM,
        estimatedKOL: formData.estimatedKOL,
        broadcastMultiplier: formData.broadcastMultiplier,
        shortlistMultiplier: formData.shortlistMultiplier,
        objectives: formData.objectives || undefined,
        targetAudience: formData.targetAudience || undefined,
        keyMessages: formData.keyMessages || undefined,
        contentTypes: contentTypes.length ? contentTypes : undefined,
        hashtags: hashtags.length ? hashtags : undefined,
        complianceRequirements: formData.complianceRequirements || undefined,
        deliverablesChecklist: deliverablesChecklist.length ? deliverablesChecklist : undefined,
        managedByOrgId: formData.managedByOrgId || undefined,
      };

      if (isEditMode && editKey) {
        await api.put(`/jobs/${encodeURIComponent(editKey)}`, payload);
        await api.post(`/jobs/${encodeURIComponent(editKey)}/tags`, { tagIds: formData.tagIds });

        const currentJobRes = await api.get(`/jobs/${encodeURIComponent(editKey)}`);
        const serverProducts = currentJobRes.data.products || [];
        const serverProductIds = serverProducts.map((p: any) => p.id);
        const formProductIds = formData.products.filter((p) => p.id).map((p) => p.id);

        for (const sp of serverProducts) {
          if (!formProductIds.includes(sp.id)) {
            await api.delete(`/jobs/${encodeURIComponent(editKey)}/products/${sp.id}`);
          }
        }

        for (const product of formData.products) {
          if (product.title) {
            if (product.id && serverProductIds.includes(product.id)) {
              await api.put(`/jobs/${encodeURIComponent(editKey)}/products/${product.id}`, {
                title: product.title,
                description: product.description,
                imageUrls: product.imageUrls,
                videoLinks: product.videoLinks,
                ecommerceLinks: product.ecommerceLinks,
              });
            } else {
              await api.post(`/jobs/${encodeURIComponent(editKey)}/products`, product);
            }
          }
        }

        navigate(`${jobsBase}/${jobUrlSegment(currentJobRes.data)}`);
      } else {
        const jobResponse = await api.post('/jobs', {
          ...payload,
          status: 'DRAFT',
          tagIds: formData.tagIds,
          targetDemographics: Object.keys(targetDemographics).length > 0 ? targetDemographics : null,
        });
        const jobId = jobResponse.data.id;
        for (const product of formData.products) {
          if (product.title) {
            await api.post(`/jobs/${jobId}/products`, product);
          }
        }
        if (formData.googleBriefUrl?.trim()) {
          try {
            await api.post(`/google-workspace/jobs/${jobId}/documents`, {
              url: formData.googleBriefUrl.trim(),
              docType: 'document',
            });
          } catch (e) {
            console.warn('Failed to link Google brief on create:', e);
          }
        }
        if (!isBrandUser && formData.applyGoogleTemplates) {
          try {
            await api.post(`/google-workspace/jobs/${jobId}/apply-templates`);
          } catch (e) {
            console.warn('Failed to apply Google templates:', e);
          }
        }
        let published = false;
        if (options?.publish && !isBrandUser) {
          try {
            await api.put(`/jobs/${encodeURIComponent(jobId)}/status`, { status: 'OPEN' });
            published = true;
          } catch (publishError) {
            console.warn('Campaign saved as draft; publish step failed:', publishError);
          }
        }
        const qs = published ? '?tab=broadcast&published=1' : '?created=1';
        navigate(`${jobsBase}/${jobUrlSegment(jobResponse.data)}${qs}`);
      }
    } catch (error: unknown) {
      console.error('Failed to save job:', error);
      const err = error as { response?: { data?: { error?: string }; status?: number } };
      const msg =
        err.response?.data?.error ||
        (err.response?.status === 403
          ? 'You do not have permission to create a campaign for this brand. Add the client under Brands first, or pick a brand your agency already works with.'
          : 'Could not save the campaign. Please try again.');
      setStepInlineError(msg);
    } finally {
      setLoading(false);
      setSavingAs(null);
    }
  };

  const addProduct = () => {
    setFormData({
      ...formData,
      products: [
        ...formData.products,
        { title: '', description: '', imageUrls: [], videoLinks: [], ecommerceLinks: [] },
      ],
    });
  };

  const updateProduct = (index: number, field: string, value: any) => {
    const updated = [...formData.products];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, products: updated });
  };

  const removeProduct = (index: number) => {
    setFormData({
      ...formData,
      products: formData.products.filter((_, i) => i !== index),
    });
  };

  const jobAIFields: AIAssistantField[] = [
    { key: 'briefDescription', label: 'What is this campaign about?', type: 'textarea', required: true, placeholder: 'Describe the campaign in a few sentences...' },
    { key: 'brandOrProduct', label: 'Brand or product', type: 'text', placeholder: 'e.g., Nike Air Max' },
    { key: 'targetAudience', label: 'Target audience', type: 'text', placeholder: 'e.g., Gen Z fitness enthusiasts' },
    { key: 'tone', label: 'Tone', type: 'select', options: [
      { value: 'friendly', label: 'Friendly' },
      { value: 'professional', label: 'Professional' },
      { value: 'humorous', label: 'Humorous' },
      { value: 'educational', label: 'Educational' },
    ]},
    { key: 'channels', label: 'Channels', type: 'text', placeholder: 'Instagram, TikTok, YouTube' },
    { key: 'budgetHint', label: 'Budget range', type: 'text', placeholder: 'e.g., RM 5,000 - 10,000' },
    { key: 'mustHaves', label: 'Must-haves', type: 'textarea', placeholder: 'Any specific requirements...' },
  ];

  const handleAIGenerate = async (values: Record<string, string>) => {
    try {
      setAiGenerating(true);
      setAiError(null);
      setAiResult(null);
      const res = await api.post('/ai/jobs/draft-suggestion', values);
      setAiResult(res.data.draft);
    } catch (e: any) {
      setAiError(e.response?.data?.error || 'AI generation failed');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleAIApply = () => {
    if (!aiResult) return;
    setFormData((prev) => ({
      ...prev,
      title: aiResult.title || prev.title,
      description: aiResult.description || prev.description,
      objectives: aiResult.objectives || prev.objectives,
      targetAudience: aiResult.targetAudience || prev.targetAudience,
      keyMessages: aiResult.keyMessages || prev.keyMessages,
      contentTypesStr: Array.isArray(aiResult.contentTypes) ? aiResult.contentTypes.join(', ') : prev.contentTypesStr,
      hashtagsStr: Array.isArray(aiResult.hashtags) ? aiResult.hashtags.join(', ') : prev.hashtagsStr,
      complianceRequirements: aiResult.complianceRequirements || prev.complianceRequirements,
      deliverablesLines: Array.isArray(aiResult.deliverables) ? aiResult.deliverables.join('\n') : prev.deliverablesLines,
      exampleScript: aiResult.exampleScript || prev.exampleScript,
      estimatedKOL: aiResult.estimatedKOL || prev.estimatedKOL,
    }));
    setShowAI(false);
    setAiResult(null);
  };

  const steps = [
    { number: 1, title: 'Basics' },
    { number: 2, title: 'Products' },
    { number: 3, title: 'Matching' },
    { number: 4, title: 'Review' },
  ];

  /** Same rules as Backend POST /jobs (title trim length ≥ 3, etc.). */
  const validateStep = (step: number): string | null => {
    switch (step) {
      case 1: {
        if (!formData.brandOrgId) return 'Please select a brand.';
        const t = formData.title.trim();
        if (!t) return 'Campaign title is required.';
        if (t.length < 3) return 'Campaign title must be at least 3 characters long.';
        if (!formData.description.trim()) return 'Description is required.';
        return null;
      }
      case 2:
        return null;
      case 3: {
        if (formData.estimatedKOL <= 0) return 'Estimated KOLs must be greater than zero.';
        return null;
      }
      default:
        return null;
    }
  };

  const goNext = () => {
    const err = validateStep(currentStep);
    if (err) {
      setStepInlineError(err);
      return;
    }
    setStepInlineError(null);
    setCurrentStep((s) => Math.min(4, s + 1));
  };

  /** Jump to a step: backward always allowed; forward validates each intermediate step. */
  const goToStep = (targetStep: number) => {
    if (targetStep === currentStep) return;
    if (targetStep < 1 || targetStep > 4) return;

    if (targetStep < currentStep) {
      setStepInlineError(null);
      setCurrentStep(targetStep);
      return;
    }

    for (let s = currentStep; s < targetStep; s++) {
      const err = validateStep(s);
      if (err) {
        setStepInlineError(err);
        setCurrentStep(s);
        return;
      }
    }
    setStepInlineError(null);
    setCurrentStep(targetStep);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(jobsBase)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-[2.25rem] font-semibold tracking-tightest text-glass-text">
            {isEditMode ? 'Edit campaign' : 'Create campaign'}
          </h1>
          <p className="text-glass-text-2 mt-1">
            {isEditMode
              ? 'Update campaign details'
              : isBrandUser
                ? 'Saved as a draft — your agency will publish when the campaign is ready to go live.'
                : 'Brief, targeting, then publish so you can invite KOLs in one pass.'}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowAI(true)} className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          AI Assistant
        </Button>
      </div>

      {/* Steps — clickable to jump; forward jumps validate prior steps */}
      <div className="flex items-center justify-between">
        {steps.map((step, index) => (
          <div key={step.number} className="flex items-center flex-1">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => goToStep(step.number)}
                className={`flex shrink-0 items-center gap-3 rounded-lg text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-accent2 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--glass-bg,#0a0a0f)] ${
                  currentStep === step.number ? '' : 'hover:opacity-90'
                }`}
                aria-current={currentStep === step.number ? 'step' : undefined}
                aria-label={`Go to step ${step.number}: ${step.title}`}
              >
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-medium transition-colors ${
                    currentStep > step.number
                      ? 'bg-emerald-500 text-white'
                      : currentStep === step.number
                        ? 'bg-accent2 text-white shadow-[0_0_15px_rgb(var(--accent-rgb)/var(--accent-glow-a))]'
                        : 'bg-glass-3 text-glass-text-2'
                  }`}
                >
                  {currentStep > step.number ? <Check className="h-5 w-5" /> : step.number}
                </div>
                <span
                  className={`truncate text-sm font-medium ${
                    currentStep >= step.number ? 'text-glass-text' : 'text-glass-text-3'
                  }`}
                >
                  {step.title}
                </span>
              </button>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-4 ${
                  currentStep > step.number ? 'bg-emerald-500' : 'bg-glass-3'
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="p-6">
          {/* Step 1: Basics */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-glass-text mb-4">Campaign basics</h2>

              {isBrandUser ? (
                <div>
                  <label className="block text-sm font-medium text-glass-text mb-1">Brand</label>
                  <p className="rounded-xl border border-glass bg-glass-2 px-3 py-2 text-sm text-glass-text">
                    {brands.find((b) => b.id === formData.brandOrgId)?.name || 'Your organization'}
                  </p>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-glass-text mb-1">
                      Managing agency (optional)
                    </label>
                    <Select
                      value={formData.managedByOrgId}
                      onChange={(e) => setFormData({ ...formData, managedByOrgId: e.target.value })}
                    >
                      <option value="">Not set — choose if you already work with an agency</option>
                      {agencyOptions.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-glass-text mb-1">Brand *</label>
                  <Select
                    value={formData.brandOrgId}
                    onChange={(e) => setFormData({ ...formData, brandOrgId: e.target.value })}
                  >
                    <option value="">Select brand</option>
                    {brands.map((corp) => (
                      <option key={corp.id} value={corp.id}>
                        {corp.name}
                      </option>
                    ))}
                  </Select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-glass-text mb-1">Campaign title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Summer Product Launch Campaign"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-glass-text mb-1">Description *</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={5}
                  placeholder="Describe the campaign objectives, target audience, and key messages..."
                />
              </div>

              {isBrandUser && !isEditMode && (
                <div>
                  <label className="block text-sm font-medium text-glass-text mb-1">
                    Campaign brief Google Doc (optional)
                  </label>
                  <Input
                    value={formData.googleBriefUrl}
                    onChange={(e) => setFormData({ ...formData, googleBriefUrl: e.target.value })}
                    placeholder={googleUrlPlaceholder('document')}
                  />
                  <p className="text-xs text-glass-text-2 mt-1">
                    Paste your brief Google Doc link — your agency can preview and import it into kol.my.
                  </p>
                </div>
              )}

              {!isBrandUser && !isEditMode && (
                <label className="flex items-start gap-2 text-sm text-glass-text-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={formData.applyGoogleTemplates}
                    onChange={(e) =>
                      setFormData({ ...formData, applyGoogleTemplates: e.target.checked })
                    }
                  />
                  <span>
                    Apply agency Google Workspace templates (brief Doc, storyboard, folder) to this
                    campaign on create
                  </span>
                </label>
              )}

              <div className="rounded-xl border border-glass bg-glass-2 p-4 space-y-3">
                <h3 className="text-sm font-semibold text-glass-text">Structured campaign brief (optional)</h3>
                <div>
                  <label className="block text-xs font-medium text-glass-text-2 mb-1">Objectives</label>
                  <Textarea
                    rows={2}
                    value={formData.objectives}
                    onChange={(e) => setFormData({ ...formData, objectives: e.target.value })}
                    placeholder="What success looks like..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-glass-text-2 mb-1">Target audience</label>
                  <Textarea
                    rows={2}
                    value={formData.targetAudience}
                    onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-glass-text-2 mb-1">Key messages</label>
                  <Textarea
                    rows={2}
                    value={formData.keyMessages}
                    onChange={(e) => setFormData({ ...formData, keyMessages: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-glass-text-2 mb-1">Content types (comma-separated)</label>
                    <Input
                      value={formData.contentTypesStr}
                      onChange={(e) => setFormData({ ...formData, contentTypesStr: e.target.value })}
                      placeholder="Reel, Story, Live"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-glass-text-2 mb-1">Hashtags (comma-separated)</label>
                    <Input
                      value={formData.hashtagsStr}
                      onChange={(e) => setFormData({ ...formData, hashtagsStr: e.target.value })}
                      placeholder="#brand #campaign"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-glass-text-2 mb-1">Compliance requirements</label>
                  <Textarea
                    rows={2}
                    value={formData.complianceRequirements}
                    onChange={(e) => setFormData({ ...formData, complianceRequirements: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-glass-text-2 mb-1">Deliverables (one per line)</label>
                  <Textarea
                    rows={3}
                    value={formData.deliverablesLines}
                    onChange={(e) => setFormData({ ...formData, deliverablesLines: e.target.value })}
                    placeholder="3x IG Reels&#10;1x TikTok video"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-glass-text mb-1">Contract Start</label>
                  <Input
                    type="date"
                    value={formData.contractStart}
                    onChange={(e) => setFormData({ ...formData, contractStart: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-glass-text mb-1">Contract End</label>
                  <Input
                    type="date"
                    value={formData.contractEnd}
                    onChange={(e) => setFormData({ ...formData, contractEnd: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-glass-text mb-1">Demo Video Link</label>
                <Input
                  value={formData.demoVideoLink}
                  onChange={(e) => setFormData({ ...formData, demoVideoLink: e.target.value })}
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-glass-text mb-1">Example Script</label>
                <Textarea
                  value={formData.exampleScript}
                  onChange={(e) => setFormData({ ...formData, exampleScript: e.target.value })}
                  rows={4}
                  placeholder="Provide an example script for content creators..."
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-glass-text mb-1">Cash Reward (RM)</label>
                  <Input
                    type="number"
                    value={formData.rewardsJson.cash}
                    onChange={(e) => setFormData({
                      ...formData,
                      rewardsJson: { ...formData.rewardsJson, cash: parseInt(e.target.value) || 0 }
                    })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-glass-text mb-1">Product Reward</label>
                  <Input
                    value={formData.rewardsJson.product}
                    onChange={(e) => setFormData({
                      ...formData,
                      rewardsJson: { ...formData.rewardsJson, product: e.target.value }
                    })}
                    placeholder="e.g., Latest smartphone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-glass-text mb-1">Commission (%)</label>
                  <Input
                    type="number"
                    value={formData.rewardsJson.commission}
                    onChange={(e) => setFormData({
                      ...formData,
                      rewardsJson: { ...formData.rewardsJson, commission: parseInt(e.target.value) || 0 }
                    })}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Products */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-glass-text">Products</h2>
                  <p className="text-sm text-glass-text-2 mt-1">Optional — skip if the brief already covers the offer.</p>
                </div>
                <Button variant="outline" size="sm" onClick={addProduct}>
                  Add Product
                </Button>
              </div>

              {formData.products.length === 0 ? (
                <div className="text-center py-8 text-glass-text-3">
                  No products yet. Add SKUs if KOLs need them, or continue to matching.
                </div>
              ) : (
                <div className="space-y-6">
                  {formData.products.map((product, index) => (
                    <div key={index} className="p-4 rounded-xl border border-glass bg-glass-2 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-glass-text">Product {index + 1}</span>
                        <Button variant="ghost" size="sm" onClick={() => removeProduct(index)}>
                          Remove
                        </Button>
                      </div>
                      
                      <Input
                        placeholder="Product title"
                        value={product.title}
                        onChange={(e) => updateProduct(index, 'title', e.target.value)}
                      />
                      
                      <Textarea
                        placeholder="Product description"
                        value={product.description}
                        onChange={(e) => updateProduct(index, 'description', e.target.value)}
                        rows={2}
                      />
                      
                      <Input
                        placeholder="Image URLs (comma-separated)"
                        value={product.imageUrls.join(', ')}
                        onChange={(e) => updateProduct(index, 'imageUrls', e.target.value.split(',').map(s => s.trim()))}
                      />
                      
                      <Input
                        placeholder="E-commerce links (comma-separated)"
                        value={product.ecommerceLinks.join(', ')}
                        onChange={(e) => updateProduct(index, 'ecommerceLinks', e.target.value.split(',').map(s => s.trim()))}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Matching */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-glass-text mb-4">KOL matching</h2>
              <p className="text-sm text-glass-text-2 -mt-2 mb-4">
                Tags and demographics improve auto-match. You can still pick KOLs from your roster after publish.
              </p>

              {/* Tags optional: roster may have no content tags; matching still scores demographics / platforms. */}
              <TagSelector
                tags={tags}
                selectedTagIds={formData.tagIds}
                onChange={(selectedIds) => setFormData({ ...formData, tagIds: selectedIds })}
                label="Target KOL tags (optional)"
              />
              {rosterPreviewLoading ? (
                <p className="text-sm text-glass-text-2">Checking your roster…</p>
              ) : rosterPreview ? (
                <div className="rounded-xl border border-accent2/30 bg-accent2/10 px-4 py-3 text-sm text-accent2">
                  {formData.tagIds.length === 0 ? (
                    <p>
                      <span className="font-semibold">{rosterPreview.total}</span> KOL
                      {rosterPreview.total === 1 ? '' : 's'} on your roster. After you publish, you’ll pick them on Invite
                      {rosterPreview.total === 0 ? ' — add KOLs under Talent first.' : '.'}
                    </p>
                  ) : rosterPreview.tagged === 0 ? (
                    <p>
                      0 tagged matches of {rosterPreview.total} roster KOLs — you’ll pick manually after publish.
                    </p>
                  ) : (
                    <p>
                      {rosterPreview.tagged} tagged match{rosterPreview.tagged === 1 ? '' : 'es'} of {rosterPreview.total}{' '}
                      roster KOLs.
                    </p>
                  )}
                </div>
              ) : null}
              {formData.tagIds.length === 0 && !rosterPreview && (
                <p className="text-sm text-glass-text-2">
                  No tags selected — after you publish, pick KOLs from your roster on Invite.
                </p>
              )}

              <div className="border-t border-glass pt-6">
                <h3 className="text-lg font-medium text-glass-text mb-4">Main Demographics (Optional)</h3>
                <p className="text-sm text-glass-text-2 mb-4">
                  Select only the criteria that matter for this campaign. Leave unchecked to match all KOLs.
                </p>

                {/* Majority Race */}
                <div className="mb-6 p-4 rounded-xl border border-glass bg-glass-2">
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="checkbox"
                      id="useMajorityRace"
                      checked={formData.targetDemographics.majorityRace !== ''}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          targetDemographics: {
                            ...formData.targetDemographics,
                            majorityRace: e.target.checked ? 'malay' : '',
                          },
                        });
                      }}
                      className="w-4 h-4 rounded border-glass accent-accent2 cursor-pointer"
                    />
                    <label htmlFor="useMajorityRace" className="text-sm font-medium text-glass-text cursor-pointer">
                      Target Majority Race
                    </label>
                  </div>
                  {formData.targetDemographics.majorityRace !== '' && (
                    <div className="ml-6 grid grid-cols-3 gap-3">
                      {(['malay', 'chinese', 'indian'] as const).map((race) => (
                        <label
                          key={race}
                          className="flex items-center gap-2 p-2 rounded-lg border border-glass bg-glass-3 cursor-pointer hover:border-accent2/40 transition-colors"
                        >
                          <input
                            type="radio"
                            name="majorityRace"
                            value={race}
                            checked={formData.targetDemographics.majorityRace === race}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                targetDemographics: {
                                  ...formData.targetDemographics,
                                  majorityRace: e.target.value as any,
                                },
                              })
                            }
                            className="w-4 h-4 accent-accent2"
                          />
                          <span className="text-sm capitalize text-glass-text">{race}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Sex */}
                <div className="mb-6 p-4 rounded-xl border border-glass bg-glass-2">
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="checkbox"
                      id="useSex"
                      checked={formData.targetDemographics.sex !== ''}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          targetDemographics: {
                            ...formData.targetDemographics,
                            sex: e.target.checked ? 'male' : '',
                          },
                        });
                      }}
                      className="w-4 h-4 rounded border-glass accent-accent2 cursor-pointer"
                    />
                    <label htmlFor="useSex" className="text-sm font-medium text-glass-text cursor-pointer">
                      Target Sex Distribution
                    </label>
                  </div>
                  {formData.targetDemographics.sex !== '' && (
                    <div className="ml-6 space-y-3">
                      <div className="flex gap-3">
                        {(['male', 'female'] as const).map((sex) => (
                          <label
                            key={sex}
                            className="flex items-center gap-2 p-2 rounded-lg border border-glass bg-glass-3 cursor-pointer hover:border-accent2/40 transition-colors"
                          >
                            <input
                              type="radio"
                              name="sex"
                              value={sex}
                              checked={formData.targetDemographics.sex === sex}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  targetDemographics: {
                                    ...formData.targetDemographics,
                                    sex: e.target.value as any,
                                  },
                                })
                              }
                              className="w-4 h-4 accent-accent2"
                            />
                            <span className="text-sm capitalize text-glass-text">{sex}</span>
                          </label>
                        ))}
                      </div>
                      <div>
                        <label className="text-xs text-glass-text-2 block mb-1">
                          Threshold: More than {formData.targetDemographics.sexThreshold}% {formData.targetDemographics.sex}
                        </label>
                        <input
                          type="range"
                          min="50"
                          max="90"
                          step="5"
                          value={formData.targetDemographics.sexThreshold}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              targetDemographics: {
                                ...formData.targetDemographics,
                                sexThreshold: parseInt(e.target.value),
                              },
                            })
                          }
                          className="w-full h-2 bg-glass-3 rounded-lg appearance-none cursor-pointer accent-accent2"
                        />
                        <div className="flex justify-between text-xs text-glass-text-3 mt-1">
                          <span>50%</span>
                          <span>90%</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Age Group */}
                <div className="mb-6 p-4 rounded-xl border border-glass bg-glass-2">
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="checkbox"
                      id="useAgeGroup"
                      checked={formData.targetDemographics.ageGroup !== ''}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          targetDemographics: {
                            ...formData.targetDemographics,
                            ageGroup: e.target.checked ? 'age18_24' : '',
                          },
                        });
                      }}
                      className="w-4 h-4 rounded border-glass accent-accent2 cursor-pointer"
                    />
                    <label htmlFor="useAgeGroup" className="text-sm font-medium text-glass-text cursor-pointer">
                      Target Age Group
                    </label>
                  </div>
                  {formData.targetDemographics.ageGroup !== '' && (
                    <div className="ml-6 grid grid-cols-3 gap-3">
                      {[
                        { value: 'age18_24', label: '18-24 years' },
                        { value: 'age25_34', label: '25-34 years' },
                        { value: 'age35_plus', label: '35+ years' },
                      ].map((age) => (
                        <label
                          key={age.value}
                          className="flex items-center gap-2 p-2 rounded-lg border border-glass bg-glass-3 cursor-pointer hover:border-accent2/40 transition-colors"
                        >
                          <input
                            type="radio"
                            name="ageGroup"
                            value={age.value}
                            checked={formData.targetDemographics.ageGroup === age.value}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                targetDemographics: {
                                  ...formData.targetDemographics,
                                  ageGroup: e.target.value as any,
                                },
                              })
                            }
                            className="w-4 h-4 accent-accent2"
                          />
                          <span className="text-sm text-glass-text">{age.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-glass-text mb-1">Total Budget (RM)</label>
                  <Input
                    type="number"
                    value={formData.totalBudgetRM}
                    onChange={(e) => setFormData({ ...formData, totalBudgetRM: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-glass-text mb-1">Estimated KOLs *</label>
                  <Input
                    type="number"
                    value={formData.estimatedKOL}
                    onChange={(e) => setFormData({ ...formData, estimatedKOL: parseInt(e.target.value) || 10 })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-glass-text mb-1">Broadcast Multiplier</label>
                  <Input
                    type="number"
                    value={formData.broadcastMultiplier}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        broadcastMultiplier: Math.max(1, parseInt(e.target.value) || 3),
                      })
                    }
                  />
                  <p className="text-xs text-glass-text-3 mt-1">
                    Will send to {formData.estimatedKOL * formData.broadcastMultiplier} KOLs
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-glass-text mb-1">Shortlist Multiplier</label>
                  <Input
                    type="number"
                    value={formData.shortlistMultiplier}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        shortlistMultiplier: Math.max(1, parseInt(e.target.value) || 1),
                      })
                    }
                  />
                  <p className="text-xs text-glass-text-3 mt-1">
                    Builds an accepted review pool up to {formData.estimatedKOL * formData.shortlistMultiplier}; final production shortlist target is {formData.estimatedKOL}
                  </p>
                  {formData.estimatedKOL * formData.shortlistMultiplier >
                    formData.estimatedKOL * formData.broadcastMultiplier && (
                    <p className="text-xs text-amber-500 dark:text-amber-300 mt-1 font-medium">
                      Warning: accepted review pool ({formData.estimatedKOL * formData.shortlistMultiplier}) exceeds
                      broadcast target ({formData.estimatedKOL * formData.broadcastMultiplier}). Consider reducing the
                      shortlist multiplier.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-glass-text mb-4">Review</h2>
              {!isBrandUser && !isEditMode && (
                <p className="text-sm text-glass-text-2 -mt-2">
                  Draft stays private. Publish unlocks inviting KOLs — they only see the campaign after you send invitations.
                </p>
              )}

              <div className="space-y-4">
                <div className="p-4 rounded-xl border border-glass bg-glass-2">
                  <h3 className="font-medium text-glass-text mb-2">Basic Information</h3>
                  <div className="space-y-1 text-sm text-glass-text">
                    <p><span className="text-glass-text-3">Title:</span> {formData.title}</p>
                    <p><span className="text-glass-text-3">Brand:</span> {brands.find((c) => c.id === formData.brandOrgId)?.name}</p>
                    <p><span className="text-glass-text-3">Description:</span> {formData.description.substring(0, 100)}...</p>
                  </div>
                </div>

                <div className="p-4 rounded-xl border border-glass bg-glass-2">
                  <h3 className="font-medium text-glass-text mb-2">Products</h3>
                  <p className="text-sm text-glass-text-2">{formData.products.length} product(s) added</p>
                </div>

                <div className="p-4 rounded-xl border border-glass bg-glass-2">
                  <h3 className="font-medium text-glass-text mb-2">Matching Criteria</h3>
                  <div className="space-y-1 text-sm text-glass-text">
                    <p><span className="text-glass-text-3">Tags:</span> {formData.tagIds.length} selected</p>
                    <p><span className="text-glass-text-3">Estimated KOLs:</span> {formData.estimatedKOL}</p>
                    <p><span className="text-glass-text-3">Will broadcast to:</span> {formData.estimatedKOL * formData.broadcastMultiplier} KOLs</p>
                    <p><span className="text-glass-text-3">Accepted review pool:</span> {formData.estimatedKOL * formData.shortlistMultiplier} KOLs</p>
                    <p><span className="text-glass-text-3">Production shortlist target:</span> {formData.estimatedKOL} KOLs</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {stepInlineError ? (
        <div
          role="alert"
          className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-700 dark:text-rose-200"
        >
          {stepInlineError}
        </div>
      ) : null}

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => {
            setStepInlineError(null);
            setCurrentStep(Math.max(1, currentStep - 1));
          }}
          disabled={currentStep === 1}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Previous
        </Button>

        {currentStep < 4 ? (
          <Button onClick={goNext}>
            Next
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        ) : isEditMode ? (
          <Button onClick={() => handleSubmit()} disabled={loading}>
            {loading ? 'Saving...' : 'Save changes'}
          </Button>
        ) : isBrandUser ? (
          <Button onClick={() => handleSubmit()} disabled={loading}>
            {loading ? 'Saving...' : 'Save draft campaign'}
          </Button>
        ) : (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button variant="outline" onClick={() => handleSubmit()} disabled={loading}>
              {loading && savingAs === 'draft' ? 'Saving...' : 'Save as draft'}
            </Button>
            <Button onClick={() => handleSubmit({ publish: true })} disabled={loading}>
              {loading && savingAs === 'publish' ? 'Publishing...' : 'Publish & invite KOLs'}
            </Button>
          </div>
        )}
      </div>

      <AIAssistantShell
        open={showAI}
        onClose={() => setShowAI(false)}
        title="AI Campaign Brief Generator"
        description="Describe your campaign and we'll generate a complete brief"
        fields={jobAIFields}
        onGenerate={handleAIGenerate}
        generating={aiGenerating}
        error={aiError}
        result={aiResult ? (
          <div className="space-y-2 text-sm">
            <p><strong>Title:</strong> {aiResult.title}</p>
            <p><strong>Description:</strong> {aiResult.description?.substring(0, 200)}...</p>
            {aiResult.suggestedTags?.length > 0 && <p><strong>Tags:</strong> {aiResult.suggestedTags.join(', ')}</p>}
          </div>
        ) : undefined}
        applyLabel="Apply to form"
        onApply={handleAIApply}
      />
    </div>
  );
}
